import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/supabase';

interface ActivityEvent {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  attendees: string;
  duration: string;
  category: string;
  color_scheme: string;
  image_url: string;
  video_url: string | null;
  youtube_url: string | null;
  media_type: 'image' | 'video' | 'youtube';
  carousel_order: number;
  is_active: boolean;
}

const ActivitiesCarousel: React.FC = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [youtubeCompleted, setYoutubeCompleted] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const youtubeRef = useRef<HTMLIFrameElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    loadEvents();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    if (isPlaying && events.length > 0) {
      const currentEventData = events[currentIndex];
      if (!currentEventData) return;
      const shouldWait = currentEventData.media_type === 'video' && !videoCompleted;
      if (shouldWait) return;
      intervalRef.current = setInterval(() => { nextSlide(); }, 5200);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, currentIndex, events.length, videoCompleted, youtubeCompleted]);

  useEffect(() => {
    setVideoCompleted(false);
    setYoutubeCompleted(false);
  }, [currentIndex]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      const data = await db.activities.getActive();
      const sorted = data.sort((a, b) => a.carousel_order - b.carousel_order);
      setEvents(sorted);
    } catch (error) {
      console.error('Error loading events:', error);
      setEvents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const nextSlide = (manual = false) => {
    if (manual) {
      setCurrentIndex((prev) => (prev + 1) % events.length);
      return;
    }
    if (events.length === 0) return;
    const ev = events[currentIndex];
    if (!ev) return;
    const auto = ev.media_type === 'image' || (ev.media_type === 'video' && videoCompleted);
    if (auto) setCurrentIndex((prev) => (prev + 1) % events.length);
  };

  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  const goToSlide = (i: number) => setCurrentIndex(i);
  const togglePlayPause = () => setIsPlaying(!isPlaying);

  const getYoutubeEmbedUrl = (url: string) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/,
    ];
    for (const p of patterns) {
      const m = url.match(p);
      if (m && m[1] && m[1].length >= 8) return `https://www.youtube.com/embed/${m[1]}`;
    }
    return null;
  };

  const handleVideoEnded = () => setVideoCompleted(true);
  const handleVideoPlay = () => setVideoCompleted(false);

  if (isLoading) {
    return (
      <section id="activities" className="section-pad surface-dark">
        <div className="container-tight flex items-center justify-center h-96">
          <div className="h-10 w-10 rounded-full border border-white/20 border-t-secondary animate-spin" />
        </div>
      </section>
    );
  }

  if (events.length === 0) {
    return (
      <section id="activities" className="section-pad surface-dark">
        <div className="container-tight text-center">
          <h2 className="font-display font-bold text-white mb-4 text-3xl md:text-4xl">{t('activities.carousel.title')}</h2>
          <p className="text-white/60">{t('activities.carousel.empty')}</p>
        </div>
      </section>
    );
  }

  const currentEvent = events[currentIndex];

  return (
    <section id="activities" className="section-pad surface-dark grain relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(hsl(0 0% 100%) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100%) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
        aria-hidden
      />

      <div className="container-tight relative z-10">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="lg:col-span-6"
          >
            <h2 className="font-display font-bold text-white" style={{ fontSize: 'clamp(2.25rem, 4vw, 3.5rem)', lineHeight: 1.02, letterSpacing: '-0.025em' }}>
              {t('activities.carousel.title')}
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            viewport={{ once: true }}
            className="lg:col-span-5 lg:col-start-8 text-white/70 lede pt-2"
          >
            {t('activities.carousel.description')}
          </motion.p>
        </div>

        {/* Carousel */}
        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[hsl(var(--primary-deep))]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Media */}
            <div className="lg:col-span-8 relative aspect-video bg-black">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentEvent.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  {currentEvent.media_type === 'image' && (
                    <img
                      src={currentEvent.image_url || '/placeholder-activity.jpg'}
                      alt={currentEvent.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  {currentEvent.media_type === 'youtube' && (
                    <iframe
                      ref={youtubeRef}
                      src={`${getYoutubeEmbedUrl(currentEvent.youtube_url || '') || ''}?autoplay=1&mute=1&controls=1&playsinline=1&rel=0&showinfo=0`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      title={currentEvent.title}
                    />
                  )}
                  {currentEvent.media_type === 'video' && (
                    <video
                      ref={videoRef}
                      src={currentEvent.video_url || ''}
                      className="w-full h-full object-cover"
                      controls
                      controlsList="nodownload noremoteplayback"
                      preload="metadata"
                      poster={currentEvent.image_url || ''}
                      onEnded={handleVideoEnded}
                      onPlay={handleVideoPlay}
                      autoPlay
                      muted
                      playsInline
                    />
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white text-primary transition-all duration-300 flex items-center justify-center z-10"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => nextSlide(true)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/85 hover:bg-white text-primary transition-all duration-300 flex items-center justify-center z-10"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>

              {currentEvent.media_type === 'video' && (
                <button
                  onClick={togglePlayPause}
                  className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/85 hover:bg-white text-primary flex items-center justify-center transition-colors"
                  aria-label={isPlaying ? 'Pause' : 'Play'}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
              )}
            </div>

            {/* Content */}
            <div className="lg:col-span-4 p-8 md:p-10 lg:p-12 flex flex-col justify-between bg-[hsl(var(--primary-deep))]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentEvent.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.4 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="inline-flex items-center font-display text-xs font-semibold uppercase tracking-[0.2em] text-secondary">
                      {currentEvent.category}
                    </span>
                    <span className="font-display text-xs text-white/40 tabular-nums">
                      {String(currentIndex + 1).padStart(2, '0')} / {String(events.length).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-white leading-tight mb-5">
                    {currentEvent.title}
                  </h3>
                  <p className="text-white/70 text-sm md:text-base leading-relaxed">
                    {currentEvent.description}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex items-center gap-2 mt-10">
                {events.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goToSlide(i)}
                    className={`h-0.5 transition-all duration-300 ${
                      i === currentIndex ? 'w-10 bg-secondary' : 'w-5 bg-white/25 hover:bg-white/50'
                    }`}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActivitiesCarousel;
